import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Megaphone, Plus, Edit3, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { TargetingGrid } from "./AdminFaqPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USER_TYPES = ["me_pilot", "pilot_host", "subscribed_host", "investor", "advocate", "team"];
const PORTALS = ["me", "host", "advocate", "investor", "team", "admin"];
const CATEGORIES = ["makeup", "hair", "lashes", "nails", "culinary", "wellness", "travel", "style", "dispensary", "events", "instructors", "style"];

export default function AdminUpdatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/updates`, { headers: auth() });
      if (r.ok) setItems((await r.json()).items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setCreating(true);
    setDraft({ title: "", body: "", target_user_types: [], target_categories: [], target_portals: [], is_published: true });
  };
  const startEdit = (u) => { setEditingId(u.update_id); setDraft({ ...u }); };

  const save = async () => {
    const method = creating ? "POST" : "PUT";
    const url = creating ? `${API}/admin/updates` : `${API}/admin/updates/${editingId}`;
    const r = await fetch(url, { method, headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify(draft) });
    if (r.ok) { toast.success("Saved"); setCreating(false); setEditingId(null); setDraft({}); load(); } else toast.error("Save failed");
  };
  const remove = async (uid) => {
    if (!window.confirm("Delete?")) return;
    const r = await fetch(`${API}/admin/updates/${uid}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success("Deleted"); load(); }
  };
  const toggleTag = (field, value) => {
    setDraft((d) => {
      const arr = d[field] || [];
      return { ...d, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };
  const editing = creating || editingId;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-updates-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Updates</h1>
        {!editing && <Button onClick={startNew} data-testid="upd-new-btn"><Plus className="w-4 h-4 mr-1" /> New Update</Button>}
      </div>

      {editing && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <Input placeholder="Title" value={draft.title || ""} onChange={e => setDraft({...draft, title: e.target.value})} data-testid="upd-title" />
            <Textarea rows={5} placeholder="Body (supports markdown)" value={draft.body || ""} onChange={e => setDraft({...draft, body: e.target.value})} data-testid="upd-body" />
            <TargetingGrid label="Show to user types" values={draft.target_user_types} options={USER_TYPES} onToggle={(v) => toggleTag("target_user_types", v)} testidPrefix="upd-ut" />
            <TargetingGrid label="Show for categories" values={draft.target_categories} options={CATEGORIES} onToggle={(v) => toggleTag("target_categories", v)} testidPrefix="upd-cat" />
            <TargetingGrid label="Show in portals" values={draft.target_portals} options={PORTALS} onToggle={(v) => toggleTag("target_portals", v)} testidPrefix="upd-portal" />
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pub" checked={!!draft.is_published} onChange={e => setDraft({...draft, is_published: e.target.checked})} data-testid="upd-published" />
              <label htmlFor="pub" className="text-xs">Published</label>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} data-testid="upd-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); setDraft({}); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-10 text-gray-400" /> : (
        <Card><CardContent className="p-2">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No updates yet.</p>
          ) : items.map(u => (
            <div key={u.update_id} className="flex items-start gap-3 p-3 border-b last:border-b-0" data-testid={`upd-row-${u.update_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{u.title} {!u.is_published && <Badge className="text-[10px] bg-gray-200 text-gray-600 border-0 ml-1">draft</Badge>}</p>
                <p className="text-xs text-gray-500 line-clamp-2">{u.body}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(u.target_user_types || []).map(t => <Badge key={t} className="text-[10px] bg-indigo-50 text-indigo-700 border-0">{t}</Badge>)}
                  {(u.target_categories || []).map(t => <Badge key={t} className="text-[10px] bg-pink-50 text-pink-700 border-0">{t}</Badge>)}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => startEdit(u)}><Edit3 className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(u.update_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
